import { useCallback, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { AtSign, CheckSquare, FileText, Inbox, MessageSquare, Pencil, Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { useDateLocale } from '@/i18n/dates'
import { useNotifications, type NotificationItem } from '@/lib/notifications/use-notifications'
import { cn } from '@/lib/utils'

type CategoryFilter = 'all' | 'notification' | 'activity'

const TABS: CategoryFilter[] = ['all', 'notification', 'activity']

function getTypeIcon(type: string) {
  switch (type) {
    case 'task_assigned':
    case 'task_comment':
      return CheckSquare
    case 'chat_message':
      return MessageSquare
    case 'document_updated':
    case 'document_comment':
      return FileText
    case 'document_mention':
    case 'comment_mention':
      return AtSign
    case 'drawing_updated':
      return Pencil
    case 'inbox_email':
    case 'inbox_run_failed':
      return Inbox
    default:
      return Bell
  }
}

function getResourceRoute(resourceType: string | null): string {
  switch (resourceType) {
    case 'task':
      return 'tasks'
    case 'chat_thread':
      return 'chat'
    case 'document':
      return 'write'
    case 'drawing':
      return 'whiteboard'
    case 'inbox_email':
      return 'inbox'
    default:
      return ''
  }
}

function formatRelativeTime(
  dateStr: string,
  t: TFunction<'notifications'>,
  intlLocale: string
): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60_000)
  const diffHour = Math.floor(diffMs / 3_600_000)
  const diffDay = Math.floor(diffMs / 86_400_000)

  if (diffMin < 1) return t('time.justNow')
  if (diffMin < 60) return t('time.minutesAgo', { count: diffMin })
  if (diffHour < 24) return t('time.hoursAgo', { count: diffHour })
  if (diffDay < 7) return t('time.daysAgo', { count: diffDay })
  return new Date(dateStr).toLocaleDateString(intlLocale)
}

interface NotificationPanelProps {
  onClose: () => void
}

export function NotificationPanel({ onClose }: NotificationPanelProps) {
  const { items, markRead, markAllRead, loadMore, hasMore } = useNotifications()
  const navigate = useNavigate()
  const { t } = useTranslation('notifications')
  const [activeTab, setActiveTab] = useState<CategoryFilter>('all')

  const filteredItems =
    activeTab === 'all' ? items : items.filter((item) => item.category === activeTab)

  const handleClick = useCallback(
    async (item: NotificationItem) => {
      if (!item.isRead) {
        await markRead(item.id)
      }

      if (item.resourceType && item.resourceId) {
        const route = getResourceRoute(item.resourceType)
        if (route) {
          if (item.resourceType === 'task') {
            // Tasks use search param ?task=<id> to open the side panel
            await navigate({
              to: `/$projectId/tasks`,
              params: { projectId: item.projectId },
              search: { task: item.resourceId },
            })
          } else if (item.resourceType === 'inbox_email') {
            await navigate({
              to: `/$projectId/inbox`,
              params: { projectId: item.projectId },
              search: { email: item.resourceId },
            })
          } else {
            // A document mention carries the block id, a comment mention the thread id:
            // the route's hash effect scrolls to it (and opens the thread).
            await navigate({
              to: `/$projectId/${route}/${item.resourceId}`,
              params: { projectId: item.projectId },
              ...(item.anchor ? { hash: item.anchor } : {}),
            })
          }
        }
      }
      onClose()
    },
    [markRead, navigate, onClose]
  )

  return (
    <div className="flex flex-col max-h-[500px]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">{t('panel.title')}</h3>
        <button
          onClick={() => markAllRead(activeTab === 'all' ? undefined : activeTab)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {t('panel.markAllRead')}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-3 py-2 border-b border-border">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-2.5 py-1 text-xs font-medium rounded-md transition-colors',
              activeTab === tab
                ? 'bg-accent text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
            )}
          >
            {t(`tabs.${tab}`)}
          </button>
        ))}
      </div>

      {/* Items list */}
      <div className="flex-1 overflow-y-auto">
        {filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Bell className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm">{t('panel.empty')}</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredItems.map((item) => (
              <NotificationRow key={item.id} item={item} onClick={() => handleClick(item)} />
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMore && filteredItems.length > 0 && (
          <div className="px-4 py-3 border-t border-border">
            <button
              onClick={loadMore}
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('panel.loadMore')}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function NotificationRow({ item, onClick }: { item: NotificationItem; onClick: () => void }) {
  const Icon = getTypeIcon(item.type)
  const isNotification = item.category === 'notification'
  const { t } = useTranslation('notifications')
  const { intl } = useDateLocale()

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-accent/50 transition-colors',
        !item.isRead && isNotification && 'bg-accent/30',
        !item.isRead && !isNotification && 'bg-accent/15'
      )}
    >
      {/* Unread indicator + Icon */}
      <div className="relative flex-shrink-0 mt-0.5">
        <Icon className="h-4 w-4 text-muted-foreground" />
        {!item.isRead && (
          <span
            className={cn(
              'absolute -left-2 top-1 h-1.5 w-1.5 rounded-full',
              isNotification ? 'bg-primary' : 'bg-destructive'
            )}
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm leading-tight truncate',
            !item.isRead && isNotification ? 'font-semibold text-foreground' : 'text-foreground'
          )}
        >
          {item.title}
        </p>
        {item.body && <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.body}</p>}
        <div className="flex items-center gap-2 mt-1">
          {item.projectName && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
              {item.projectName}
            </span>
          )}
          <span className="text-[10px] text-muted-foreground">
            {/* The newest event's time — title/body describe it, and the list is ordered by it. */}
            {formatRelativeTime(item.notifiedAt, t, intl)}
          </span>
        </div>
      </div>
    </button>
  )
}
